const SRC_PREFERENCE = ['standard', 'high', 'medium', 'default', 'maxres'] as const;
const SRCSET_CANDIDATES = [
  ['medium', 320],
  ['maxres', 1280],
  ['default', 120],
  ['high', 480],
  ['standard', 640],
] as const;
const LINKED_DATA_ORDER = ['standard', 'default', 'high', 'medium', 'maxres'] as const;

type ThumbnailKey = (typeof SRC_PREFERENCE)[number];

type Thumbnail = {
  url: string;
};

export type ThumbnailSet = Partial<Record<ThumbnailKey, Thumbnail>> | null | undefined;

const toWebpUrl = (imageUrl: string): string =>
  (imageUrl && imageUrl.replace('/vi/', '/vi_webp/').replace('.jpg', '.webp')) || imageUrl;

const mqdefaultUrl = (videoId: string): string =>
  `https://i.ytimg.com/vi_webp/${videoId}/mqdefault.webp`;

const ALL_SIZES = ['default', 'standard', 'high', 'medium', 'maxres'] as const;

export const hasUsableThumbnail = (thumbnails: ThumbnailSet): boolean => {
  if (!thumbnails) return false;
  return ALL_SIZES.every(key => thumbnails[key] != null);
};

export const shouldShowPoster = (
  showPreview: boolean | undefined,
  thumbnails: ThumbnailSet,
): boolean => (showPreview ?? true) && hasUsableThumbnail(thumbnails);

export const posterSrc = (videoId: string, thumbnails: ThumbnailSet): string => {
  if (thumbnails) {
    for (const key of SRC_PREFERENCE) {
      const size = thumbnails[key];
      if (size) return toWebpUrl(size.url);
    }
  }
  return mqdefaultUrl(videoId);
};

export const posterSrcSet = (videoId: string, thumbnails: ThumbnailSet): string => {
  if (thumbnails) {
    const candidates = SRCSET_CANDIDATES.flatMap(([key, width]) => {
      const size = thumbnails[key];
      return size ? [`${toWebpUrl(size.url)} ${width}w`] : [];
    });
    if (candidates.length > 0) return candidates.join(', ');
  }
  return `${mqdefaultUrl(videoId)} 320w`;
};

export const linkedDataThumbnailUrls = (videoId: string, thumbnails: ThumbnailSet): string[] => {
  if (thumbnails) {
    const urls = LINKED_DATA_ORDER.flatMap(key => {
      const size = thumbnails[key];
      return size ? [size.url] : [];
    });
    if (urls.length > 0) return urls;
  }
  return [mqdefaultUrl(videoId)];
};
